import type { OrderBy } from '@components/Table/MutationTable/useMutationTableVM';

/**
 * État persistant d'un tableau paginé (recherche, page, lignes par page, tri),
 * conservé dans l'URL de la liste (retour navigateur) et en sessionStorage
 * (bouton « Retour » des fiches, qui pointe sur une URL fixe).
 */
export type PersistedTableState = {
    search: string | null;
    page: number;
    limit: number;
    orderBy: OrderBy;
};

export const DEFAULT_LIMIT = 10;

const URL_KEYS = { search: 'q', page: 'page', limit: 'limit', orderBy: 'tri' } as const;

const storageKey = (persistKey: string) => `cosem.table.${persistKey}`;

const parseOrderBy = (raw: string | null | undefined): OrderBy => {
    if (!raw) return null;
    const [field, direction] = raw.split(':');
    if (!field || (direction !== 'asc' && direction !== 'desc')) return null;
    return { field, direction };
};

const serializeOrderBy = (orderBy: OrderBy): string | null => (orderBy ? `${orderBy.field}:${orderBy.direction}` : null);

const positiveInt = (raw: string | number | null | undefined, fallback: number): number => {
    const n = Number(raw);
    return Number.isInteger(n) && n > 0 ? n : fallback;
};

const hasUrlState = (params: URLSearchParams): boolean => Object.values(URL_KEYS).some((key) => params.has(key));

const fromUrl = (params: URLSearchParams, defaults: PersistedTableState): PersistedTableState => ({
    search: params.get(URL_KEYS.search) || null,
    page: positiveInt(params.get(URL_KEYS.page), 1),
    limit: positiveInt(params.get(URL_KEYS.limit), defaults.limit),
    orderBy: parseOrderBy(params.get(URL_KEYS.orderBy)) ?? defaults.orderBy
});

const fromStorage = (persistKey: string, defaults: PersistedTableState): PersistedTableState | null => {
    try {
        const raw = window.sessionStorage.getItem(storageKey(persistKey));
        if (!raw) return null;
        const saved = JSON.parse(raw) as Partial<PersistedTableState>;
        return {
            search: typeof saved.search === 'string' && saved.search !== '' ? saved.search : null,
            page: positiveInt(saved.page, 1),
            limit: positiveInt(saved.limit, defaults.limit),
            orderBy: parseOrderBy(serializeOrderBy(saved.orderBy ?? null)) ?? defaults.orderBy
        };
    } catch {
        return null;
    }
};

/** État initial : l'URL prime, puis la session, puis les valeurs par défaut. */
export const readPersistedTableState = (persistKey: string | undefined, params: URLSearchParams, defaultOrderBy: OrderBy): PersistedTableState => {
    const defaults: PersistedTableState = { search: null, page: 1, limit: DEFAULT_LIMIT, orderBy: defaultOrderBy };
    if (!persistKey) return defaults;
    if (hasUrlState(params)) return fromUrl(params, defaults);
    return fromStorage(persistKey, defaults) ?? defaults;
};

/** Recopie l'état dans les paramètres d'URL (seules les valeurs non par défaut apparaissent) et en session. */
export const writePersistedTableState = (persistKey: string, params: URLSearchParams, state: PersistedTableState, defaultOrderBy: OrderBy): URLSearchParams => {
    const next = new URLSearchParams(params);
    Object.values(URL_KEYS).forEach((key) => next.delete(key));
    if (state.search) next.set(URL_KEYS.search, state.search);
    if (state.page > 1) next.set(URL_KEYS.page, String(state.page));
    if (state.limit !== DEFAULT_LIMIT) next.set(URL_KEYS.limit, String(state.limit));
    if (serializeOrderBy(state.orderBy) !== serializeOrderBy(defaultOrderBy) && state.orderBy) next.set(URL_KEYS.orderBy, serializeOrderBy(state.orderBy) as string);
    try {
        window.sessionStorage.setItem(storageKey(persistKey), JSON.stringify(state));
    } catch {
        // stockage indisponible (navigation privée stricte) : l'URL suffit
    }
    return next;
};
